var $ = require("jquery");
var DOMPurify = require("dompurify");

var supportedLanguages = {
  "de-DE": true,
  "en-US": true,
  "es-ES": true,
  "fr-CA": true,
  "fr-FR": true,
  "it-IT": true,
  "ja-JP": true,
  "nl-NL": true,
  "pt-BR": true
};

var Internationalization = function() {
  this.setup();
};

/**
 * Setup the dictionary for internationalization and localization.
 */
Internationalization.prototype.setup = function() {
  this.reset();
};

/**
 * Reset the translation entries.
 */
Internationalization.prototype.reset = function() {
  this.dictionary = {};
  this.supportedLanguages = supportedLanguages;
  this.defaultLanguage = "en-US";
};

/**
 * Check to see if language is supported
 */
Internationalization.prototype.isLanguageSupported = function(lang) {
  if (lang.length === 2) {
    const langWithTwoChars = Object.keys(supportedLanguages).find((localKey) => localKey.startsWith(lang));
    return supportedLanguages[langWithTwoChars] || false;
  }

  const parsedLang = this.convertUnderscoreToKebab(lang);

  return supportedLanguages[parsedLang] || false;
};

/**
 * If there is no region i.e. en instead of en-US
 * in language then just return the first
 * default one that is supported in the language list
 */
Internationalization.prototype.parseLanguageCode = function(lang) {
  let finalLang = lang;

  if (lang.length === 2) {
    let supportedKey = Object.keys(supportedLanguages).find((localeKey) => localeKey.startsWith(lang));

    if (supportedKey) {
      finalLang = supportedKey;
    }
  }

  return finalLang;
};

/**
 * Prevent special characters getting into the locale code
 * @param lang i.e. it_IT, it-IT, en-US, en_US etc...
 * @return boolean
 */
Internationalization.prototype.validateLangCode = function(lang) {
  const reggy = new RegExp("^[A-Za-z]{2,4}([_-][A-Za-z]{4})?([_-]([A-Za-z]{2}|[0-9]{3}))?$");

  if (!reggy.test(lang)) {
    return false;
  }

  return true;
};

/**
 * Load the translation entries.
 *
 * @param dictionary the dictionary object
 */
Internationalization.prototype.load = function(dictionary, lang) {
  var langDictionary = this.getLangDictionary(lang);

  Object.keys(dictionary).forEach(function(key) {
    const entry = dictionary[key];
    if (entry == null) {
      // Delete dictionary entry if it's set as null or undefined
      delete langDictionary[key];

    } else if (this.isObject(entry)) {
      // Warn if the dictionary entry is not of a primitive type
      console.warn("Dictionary entry \"" + key + "\" is not of a primitive type and will be discarded");

    } else {
      // Add/replace the dictionary entry otherwise
      langDictionary[key] = dictionary[key];
    }
  }, this);

  this.setLangDictionary(langDictionary, lang);
};

/**
 * Whether the value is an object.
 *
 * @param value the value to be test
 *
 * @return true if value is an object
 */
Internationalization.prototype.isObject = function(value) {
  var type = typeof value;
  return !!value && (type === "object" || type === "function");
};

/**
 * Get the dictionary for that specified language;
 *
 * @param lang the language to get the dictionary
 *
 * @return the dictionary
 */
Internationalization.prototype.getLangDictionary = function(lang) {
  return this.dictionary[lang] || {};
};

/**
 * Set the language dictionary.
 *
 * @param dictionary the dictionary
 * @param lang the language
 */
Internationalization.prototype.setLangDictionary = function(dictionary, lang) {
  this.dictionary[lang] = dictionary;
};

/**
 * Set the default language.
 *
 * @param language the language
 */
Internationalization.prototype.setDefaultLanguage = function(language) {
  this.defaultLanguage = language;
};

/**
 * Get the default language.
 *
 * @return the default language
 */
Internationalization.prototype.getDefaultLanguage = function() {
  return this.defaultLanguage;
};

/**
 * Set the customer language.
 *
 * @param language the customer language
 */
Internationalization.prototype.setCustomerLanguage = function(language) {
  this.customerLanguage = language || null;
};

/**
 * Get the customer language.
 *
 * @return the customer language
 */
Internationalization.prototype.getCustomerLanguage = function() {
  return this.customerLanguage;
};

/**
 * Get the current language.
 *
 * @return the customer language and also considering the default language
 */
Internationalization.prototype.getCurrentLanguage = function() {
  return this.getCustomerLanguage() || this.getDefaultLanguage();
};

/**
 * Sets documentElement.lang A.K.A <html lang=${locale}>
 * @param {locale} str
 * @returns void
 */
Internationalization.prototype.setDocumentElementLang = function(locale, document) {
  let localeToSet = locale;

  if (!locale) {
    localeToSet = this.defaultLanguage || "en-US";
  }

  document.documentElement.setAttribute("lang", localeToSet);
};

/**
 * Converts underscore to kebab case
 */
Internationalization.prototype.convertUnderscoreToKebab = function(str) {
  if (!str) {
    return false;
  }

  if (!str.includes("_")) {
    if (str.includes("-")) {
      return str;
    }

    return false;
  }

  const strToArray = str.split("_");
  const joinArrayToKebabStr = strToArray.join("-");

  return joinArrayToKebabStr;
};

/**
 * Get current language from document.location.search value
 *
 * @returns false | string (the locale value)
 */
Internationalization.prototype.getCurrentLanguageFromSearch = function(document) {
  if (!document && !document.location && !document.location.search) {
    return false;
  }

  const searchParams = new URLSearchParams(document.location.search);

  if (!searchParams.has("locale")) {
    return false;
  }

  const locale = decodeURIComponent(searchParams.get("locale"));
  const localeToKebabs = this.convertUnderscoreToKebab(locale);

  if (!localeToKebabs || !Object.prototype.hasOwnProperty.call(this.supportedLanguages, localeToKebabs)) {
    return this.defaultLanguage || "en-US";
  }

  return localeToKebabs;
};

/**
 * Get the current lang value from html element
 *
 * @returns the html lang attribute
 */
Internationalization.prototype.getCurrentLanguageAttribute = function(document) {
  if (!document || !document.documentElement || !document.documentElement.lang) {
    return this.defaultLanguage || "en-US";
  }

  if (!this.supportedLanguages[document.documentElement.lang]) {
    return this.defaultLanguage || "en-US";
  }

  return document.documentElement.lang || this.defaultLanguage || "en-US";
};

/**
 * Translate a given fragment.
 *
 * @param $fragment the jQuery object
 *
 * @return jQuery Promise
 */
Internationalization.prototype.translateFragment = function($fragment) {
  this.translateFragmentStrings($fragment);
  this.translateFragmentTitles($fragment);
  this.translateFragmentValues($fragment);
  this.translateFragmentAriaLabels($fragment);

  return $fragment;
};

/**
 * Translate strings in a given fragment.
 *
 * @param $fragment the jQuery object
 *
 * @return the translated fragment
 */
Internationalization.prototype.translateFragmentStrings = function($fragment) {
  return $fragment.find("[data-i18n-string]").each(function(i, el) {
    let $el = $(el);
    let key = $el.data("i18n-string");
    let cleanHTML = DOMPurify.sanitize(this.t(key));

    $el.html(cleanHTML);
  }.bind(this));
};

/**
 * Translate titles in a given fragment.
 *
 * @param $fragment the jQuery object
 *
 * @return the translated fragment
 */
Internationalization.prototype.translateFragmentTitles = function($fragment) {
  return $fragment.find("[data-i18n-title]").each(function(i, el) {
    var $el = $(el);
    var key = $el.data("i18n-title");

    $el.attr("title", this.t(key));
  }.bind(this));
};

/**
 * Translate titles in a given fragment.
 *
 * @param $fragment the jQuery object
 *
 * @return the translated fragment
 */
Internationalization.prototype.translateFragmentValues = function($fragment) {
  return $fragment.find("[data-i18n-value]").each(function(i, el) {
    var $el = $(el);
    var key = $el.data("i18n-value");

    $el.attr("value", this.t(key));
  }.bind(this));
};

/**
 * Translate aria-label attributes in a given fragment.
 *
 * @param $fragment the jQuery object
 *
 * @return the translated fragment
 */
Internationalization.prototype.translateFragmentAriaLabels = function($fragment) {
  return $fragment.find("[data-i18n-aria-label]").each(function(i, el) {
    var $el = $(el);
    var key = $el.data("i18n-aria-label");

    $el.attr("aria-label", this.t(key));
  }.bind(this));
};

/**
 * Get the translation for the key string.
 *
 * It will first try to get the entry from the custom dictionary, them from the current dictionary and finally
 * it will get the entry from the default dictionary that was loaded from the Res.strings.
 *
 * @param key the key to return the string
 *
 * @return the translated strings
 */
Internationalization.prototype.t = function(key) {
  var customDictionary = this.getLangDictionary("custom");
  var currentDictionary = this.getLangDictionary(this.getCurrentLanguage());
  var defaultDictionary = this.getLangDictionary(this.getDefaultLanguage());

  return customDictionary[key] || currentDictionary[key] || defaultDictionary[key];
};

module.exports = new Internationalization();
module.exports.Internationalization = Internationalization;
